import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import api from "@/lib/api";
import { isStudioPreview } from "@/lib/studioPreviewBridge";
import { getPluginManifest } from "@/plugins/registry";

// Hôte des plugins de la vitrine — monté une fois dans App.js, à l'intérieur
// du routeur, ne rend rien.
//
// Règles :
// - Ne démarre qu'APRÈS le premier rendu, en période d'inactivité du
//   navigateur : un plugin ne retarde jamais l'affichage de la page.
// - Un seul appel réseau (GET /api/plugins) : la liste des plugins ACTIFS
//   pour la boutique, décidée par le super admin. Rien d'actif → rien de
//   chargé, pas même le code des plugins.
// - Chaque plugin est importé dynamiquement (chunk séparé) et monté dans un
//   try/catch : une erreur de chargement ou d'exécution est ignorée, la
//   boutique continue normalement.
// - Jamais dans le back-office (/admin) ni dans l'aperçu du Studio de
//   thème : ce ne sont pas de vrais visiteurs.
//
// Contrat d'un module vitrine de plugin (`load()` du manifeste) :
//   export async function mount({ config, onRouteChange, getPath }) → { unmount? }
//   - config       : configuration publique renvoyée par le backend
//   - onRouteChange: (fn(path, search)) → désabonnement ; appelé à chaque navigation
//   - getPath      : () => chemin courant

const MANIFEST_TIMEOUT_MS = 8000;

// État module-level : survit aux re-montages React (StrictMode, HMR) pour ne
// jamais charger deux fois le même plugin.
const state = {
  started: false,
  instances: new Map(), // key → { unmount }
  routeListeners: new Set(),
  path: "/",
  search: "",
};

function isBackOffice(pathname) {
  return pathname === "/admin" || pathname.startsWith("/admin/");
}

function subscribeRoute(fn) {
  if (typeof fn !== "function") return () => {};
  state.routeListeners.add(fn);
  return () => state.routeListeners.delete(fn);
}

async function mountOne(entry) {
  const manifest = getPluginManifest(entry.key);
  if (!manifest || typeof manifest.load !== "function") return;
  if (state.instances.has(entry.key)) return;
  try {
    const mod = await manifest.load();
    const mount = mod?.mount || mod?.default?.mount;
    if (typeof mount !== "function") return;
    const instance = await mount({
      config: entry.config || {},
      onRouteChange: subscribeRoute,
      getPath: () => state.path,
    });
    state.instances.set(entry.key, instance || {});
  } catch (e) {
    /* plugin défaillant : ignoré, la boutique continue */
  }
}

async function startPlugins() {
  if (state.started) return;
  state.started = true;
  let entries = [];
  try {
    const { data } = await api.get("/plugins", { timeout: MANIFEST_TIMEOUT_MS });
    entries = Array.isArray(data?.plugins) ? data.plugins : [];
  } catch (e) {
    return; // backend injoignable ou système de plugins indisponible : rien à monter
  }
  for (const entry of entries) {
    // Séquentiel et sans await bloquant du rendu : on est déjà hors du
    // chemin critique (requestIdleCallback), l'ordre n'a pas d'importance.
    // eslint-disable-next-line no-await-in-loop
    await mountOne(entry);
  }
}

function scheduleIdle(fn) {
  if (typeof window === "undefined") return () => {};
  if (typeof window.requestIdleCallback === "function") {
    const id = window.requestIdleCallback(fn, { timeout: 3000 });
    return () => window.cancelIdleCallback?.(id);
  }
  const id = setTimeout(fn, 1500);
  return () => clearTimeout(id);
}

export default function PluginHost() {
  const location = useLocation();
  const startedHere = useRef(false);

  // Chemin courant tenu à jour AVANT de notifier les plugins.
  state.path = location.pathname;
  state.search = location.search;

  useEffect(() => {
    if (startedHere.current) return undefined;
    if (isBackOffice(location.pathname) || isStudioPreview()) return undefined;
    startedHere.current = true;
    const cancel = scheduleIdle(() => { startPlugins().catch(() => {}); });
    return cancel;
    // Démarrage unique : la première page publique visitée suffit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => {
    if (isBackOffice(location.pathname)) return;
    for (const fn of state.routeListeners) {
      try {
        fn(location.pathname, location.search);
      } catch (e) {
        /* isolé */
      }
    }
  }, [location.pathname, location.search]);

  return null;
}

// Pour les tests uniquement.
export function _resetPluginHost() {
  for (const inst of state.instances.values()) {
    try { inst?.unmount?.(); } catch (e) { /* ignore */ }
  }
  state.instances.clear();
  state.routeListeners.clear();
  state.started = false;
}
